import { createContext, useContext } from 'react';

// Form-level presentation knobs the engine reads at render time. Kept in a
// context (not prop-drilled through every section/field) so a consumer can set
// them once via `config.ui` and have every primitive pick them up. Add a new
// knob by extending `LeaseDetailsUIValue` and the default below — callers read
// via `useLeaseDetailsUI()` and destructure what they need.

export type LeaseDetailsInputSize = 'small' | 'medium';

/**
 * Form-level spacing — only knobs that govern layout *between* sections.
 * Inner-section knobs live on each section's own `SectionConfig.spacing`.
 * MUI Stack `spacing` units (1 = 8px).
 */
export type LeaseDetailsFormSpacing = {
  /** Vertical gap between adjacent visible sections. Default 3. */
  betweenSections?: number;
};

/**
 * Per-section spacing — governs layout *inside* a section. Applies to any
 * section type (lease info, screening, coverage, applicants).
 */
export type LeaseDetailsSectionSpacing = {
  /**
   * `SectionShell` outer Stack — between the section heading, slot content,
   * and field content. Default 2.
   */
  content?: number;
  /** Inside `FieldsRenderer`: between field rows. Default 2. */
  fields?: number;
  /** Inside an `option-box` field: between label, radios, and error. Default 2. */
  optionBox?: number;
};

/**
 * Applicants section spacing — extends {@link LeaseDetailsSectionSpacing}
 * with applicants-specific entries-layout keys.
 */
export type LeaseDetailsApplicantsSpacing = LeaseDetailsSectionSpacing & {
  /** Between applicant entries in the detailed layout. Default 3. */
  entries?: number;
  /** Between an entry's title and its fields. Default 2. */
  entry?: number;
};

export type LeaseDetailsUIValue = {
  inputSize: LeaseDetailsInputSize;
  asteriskColor?: string;
  /**
   * Inner-section spacing for descendants (`FieldsRenderer`,
   * `LeaseOptionBoxInput`, `ApplicantsSection`). Published by `SectionShell`
   * from each section's own `spacing` config — components consume only what
   * they need (e.g. `LeaseOptionBoxInput` reads `optionBox`).
   */
  spacing?: LeaseDetailsApplicantsSpacing;
};

const DEFAULT_UI_VALUE: LeaseDetailsUIValue = {
  inputSize: 'medium',
};

const LeaseDetailsUIContext = createContext<LeaseDetailsUIValue>(DEFAULT_UI_VALUE);

export const LeaseDetailsUIProvider = LeaseDetailsUIContext.Provider;

export const useLeaseDetailsUI = (): LeaseDetailsUIValue => useContext(LeaseDetailsUIContext);
